import React from 'react';

// mui components
import { Grid, OutlinedInputProps, TextFieldProps } from '@mui/material';
import CalendarIcon from 'assets/icons/buttons/date-picker.svg';

import { DatePicker as MuiDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import OutlinedInput from './inputs/outlined-input';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { toIsoUtcDate, utcDateToZoned } from 'utils/postDateHelper';

dayjs.extend(utc);
dayjs.extend(timezone);

interface OwnProps {
  name: string;
  error?: boolean;
  placeholder?: string;
  label?: string;
  required?: boolean;
  helperText?: string;
  value?: string;
  fullWidth?: boolean;
  icon?: boolean;
  disablePast?: boolean;
  onChange: (...event: any[]) => void;
  [x: string]: any;
  onOpen?: () => void;
  defaultCalendarMonth?: Date;
  disabled?: boolean;
}

const DatePicker: React.FC<OwnProps> = ({
  name,
  label,
  error,
  required,
  placeholder,
  helperText,
  value,
  fullWidth,
  onChange,
  maxDate,
  minDate,
  shouldDisableDate,
  icon,
  onOpen,
  defaultCalendarMonth,
  disablePast,
  disabled = false,
}) => {
  const datePickerValue = !value ? utcDateToZoned(toIsoUtcDate(new Date())) : value;
  const DateAdapter = AdapterDateFns;

  const onKeyDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleChangeInput = (event) => {
    onChange(new Date(event.target.value));
  };

  const handleChange = (val: any) => {
    if (!val) {
      onChange(null);
      return;
    }
    const iso = toIsoUtcDate(val);

    onChange(iso);
  };

  const renderInput = ({ InputProps, InputLabelProps, ...inputProps }: TextFieldProps) => (
    <Grid item className="date-picker-mui">
      <OutlinedInput
        {...(inputProps as OutlinedInputProps)}
        name={name}
        value={datePickerValue}
        label={label}
        error={error}
        required={required}
        fullWidth={fullWidth}
        helperText={helperText}
        placeholder={placeholder}
        endAdornment={InputProps?.endAdornment}
        onKeyDown={onKeyDown}
        onChange={handleChangeInput}
      />
    </Grid>
  );

  return (
    <LocalizationProvider dateAdapter={DateAdapter}>
      <MuiDatePicker
        shouldDisableDate={shouldDisableDate}
        maxDate={maxDate}
        minDate={minDate}
        value={datePickerValue}
        onChange={handleChange}
        renderInput={renderInput}
        onOpen={onOpen}
        defaultCalendarMonth={!!defaultCalendarMonth ? new Date(defaultCalendarMonth) : null}
        components={{
          OpenPickerIcon: icon && CalendarIcon,
        }}
        disablePast={!!disablePast}
        disabled={disabled}
      />
    </LocalizationProvider>
  );
};

export default DatePicker;
